"use client"

import {useEffect, useState} from "react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Separator,
  Textarea,
  useToast,
} from "@mantis-sas/ui";
import Select from "react-select";
import {Loader2} from "lucide-react";

// Importar el componente FileUploadField
import FileUploadField from "./file-upload-field";
import {submitPublicForm} from "../../../ApiCalls/customForms";

export default function FormRenderer({
  form,
  isPreview = false,
  shareId = null,
  additionalInfo,
  previousSubmission = null,
  submissionId = null
}) {
  const [formValues, setFormValues] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Add effect to populate form with previous submission data
  useEffect(() => {
    if (previousSubmission) {
      // Set form values from previous submission
      setFormValues(previousSubmission.responses || {});
    }
  }, [previousSubmission]);

  const {toast} = useToast();

  const handleInputChange = (fieldId, value) => {
    console.log(value);
    setFormValues({
      ...formValues,
      [fieldId]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validate required fields
    const requiredFields = form.fields.filter((field) => field.required);
    const missingFields = requiredFields.filter((field) => {
      // Conditional logic check: if field is hidden, it's not "missing" for validation
      if (field.conditionalLogic?.enabled) {
        const dependsOnField = form.fields.find(
          (f) => f.id === field.conditionalLogic.dependsOn
        );
        if (dependsOnField) {
          const dependsOnValue = formValues[dependsOnField.id];
          const {showWhen, value: conditionalValue} = field.conditionalLogic;
          let shouldShow = false;
          switch (showWhen) {
            case "equals":
              shouldShow = dependsOnValue === conditionalValue;
              break;
            case "not_equals":
              shouldShow = dependsOnValue !== conditionalValue;
              break;
            case "contains":
              shouldShow = dependsOnValue?.includes(conditionalValue);
              break;
            case "not_contains":
              shouldShow = !dependsOnValue?.includes(conditionalValue);
              break;
            case "is_empty":
              shouldShow = !dependsOnValue || dependsOnValue === "";
              break;
            case "is_not_empty":
              shouldShow = dependsOnValue && dependsOnValue !== "";
              break;
            default:
              shouldShow = true;
          }
          if (!shouldShow) return false; // If not shown, not missing
        }
      }
      // Actual missing check
      if (field.type === "checkbox") {
        return !formValues[field.id]; // For required checkboxes, value must be true
      }
      return !formValues[field.id];
    });

    if (missingFields.length > 0) {
      const missingFieldNames = missingFields
        .map((f) => f.label || f.id)
        .join(", ");
      toast({
        title: "Error en el formulario",
        description: `Por favor, completa todos los campos requeridos: ${missingFieldNames}.`,
        variant: "destructive",
        style: {borderRadius: "10px"}
      });
      setIsSubmitting(false);
      return;
    }

    if (isPreview) {
      toast({
        title: "Vista previa enviada",
        description: "Así se verían los datos del formulario.",
        style: {
          borderRadius: "12px",
          backgroundColor: "var(--doc-bg-secondary)"
        }
      });
      setFormSubmitted(true);
      setIsSubmitting(false);
      return;
    } else {
      try {
        const response = await submitPublicForm(
          shareId,
          formValues,
          [],
          submissionId, // Pass submissionId if it exists (for updates)
          ""
        );

        toast({
          title: submissionId
            ? "¡Formulario actualizado con éxito! 🎉"
            : "¡Formulario enviado con éxito! 🎉",
          description: (
            <div className="mt-2 space-y-2">
              <p>
                {submissionId
                  ? "Gracias por actualizar el formulario."
                  : "Gracias por completar el formulario."}
              </p>
              <p className="text-sm text-gray-500">
                Tu información ha sido{" "}
                {submissionId ? "actualizada" : "recibida"} correctamente.
              </p>
            </div>
          ),
          style: {
            borderRadius: "12px",
            backgroundColor: "white",
            border: "1px solid var(--color-secondary)",
            boxShadow: "0 4px 6px rgba(0,0,0,0.1)"
          }
        });

        setFormSubmitted(true);
      } catch (error) {
        toast({
          title: "Error al enviar",
          description: (
            <div className="mt-2 space-y-2">
              <p>
                Hubo un problema al {submissionId ? "actualizar" : "enviar"} tu
                formulario.
              </p>
              <p className="text-sm text-gray-500">
                Por favor, inténtalo de nuevo.
              </p>
            </div>
          ),
          variant: "destructive",
          style: {
            borderRadius: "12px",
            backgroundColor: "white",
            border: "1px solid #ef4444",
            boxShadow: "0 4px 6px rgba(0,0,0,0.1)"
          }
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Actualizar la función renderField para incluir el tipo "file"
  const renderField = (field) => {
    // Verificar si el campo debe mostrarse según la lógica condicional
    if (field.conditionalLogic?.enabled) {
      const dependsOnField = form.fields.find(
        (f) => f.id === field.conditionalLogic.dependsOn
      );
      if (!dependsOnField) return null;

      const dependsOnValue = formValues[dependsOnField.id];
      const {showWhen, value} = field.conditionalLogic;

      // Evaluar la condición
      let shouldShow = false;

      switch (showWhen) {
        case "equals":
          shouldShow = dependsOnValue === value;
          break;
        case "not_equals":
          shouldShow = dependsOnValue !== value;
          break;
        case "contains":
          shouldShow = dependsOnValue?.includes(value);
          break;
        case "not_contains":
          shouldShow = !dependsOnValue?.includes(value);
          break;
        case "is_empty":
          shouldShow = !dependsOnValue || dependsOnValue === "";
          break;
        case "is_not_empty":
          shouldShow = dependsOnValue && dependsOnValue !== "";
          break;
        default:
          shouldShow = true;
      }

      if (!shouldShow) return null;
    }
    switch (field.type) {
      case "text":
        return (
          <Input
            id={field.id}
            type={field.type}
            placeholder={field.placeholder}
            value={formValues[field.id] || ""}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            required={field.required}
            className="w-full h-12 bg-white border-gray-200 shadow-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
            style={{borderRadius: "12px"}}
            disabled={formSubmitted}
          />
        );
      case "email":
      case "number":
      case "tel":
        return (
          <Input
            id={field.id}
            type={field.type}
            placeholder={field.placeholder}
            value={formValues[field.id] || ""}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            required={field.required}
            className="w-full h-12 bg-white border-gray-200 shadow-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
            style={{borderRadius: "12px"}}
            disabled={formSubmitted}
          />
        );

      case "textarea":
        return (
          <Textarea
            id={field.id}
            placeholder={field.placeholder}
            value={formValues[field.id] || ""}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            required={field.required}
            className="w-full min-h-[120px] bg-white border-gray-200 shadow-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
            style={{borderRadius: "12px"}}
            disabled={formSubmitted}
          />
        );

      case "select":
        return (
          <Select
            value={
              field.options.find(
                (option) => option.value === formValues[field.id]
              ) || null
            }
            onChange={(selectedOption) =>
              handleInputChange(field.id, selectedOption?.value || "")
            }
            options={field.options}
            isDisabled={formSubmitted}
            isRequired={field.required}
            placeholder={field.placeholder || "Seleccionar opción"}
            className="react-select-container"
            classNamePrefix="react-select"
            styles={{
              control: (base) => ({
                ...base,
                backgroundColor: "white",
                borderRadius: "12px",
                borderColor: "var(--color-secondary)",
                minHeight: "48px",
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                "&:hover": {
                  borderColor: "var(--color-secondary)"
                }
              }),
              menu: (base) => ({
                ...base,
                backgroundColor: "white",
                borderRadius: "12px",
                boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                zIndex: 9999
              }),
              menuPortal: (base) => ({
                ...base,
                zIndex: 9999
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isFocused
                  ? "var(--color-secondary)"
                  : "transparent",
                color: state.isFocused ? "white" : "var(--doc-text-primary)",
                padding: "12px 16px",
                "&:active": {
                  backgroundColor: "var(--color-secondary)"
                }
              }),
              singleValue: (base) => ({
                ...base,
                color: "var(--doc-text-primary)",
                fontSize: "0.95rem"
              }),
              placeholder: (base) => ({
                ...base,
                color: "var(--doc-text-muted)",
                fontSize: "0.95rem"
              })
            }}
            isVirtualized={true}
            isSearchable={true}
            menuPortalTarget={
              typeof window !== "undefined" ? document.body : null
            }
            menuPosition="fixed"
          />
        );

      case "radio":
        return (
          <RadioGroup
            value={formValues[field.id] || ""}
            onValueChange={(value) => handleInputChange(field.id, value)}
            required={field.required}
            disabled={formSubmitted}
            className="space-y-3"
          >
            {field.options.map((option) => (
              <div
                key={option.id}
                className="flex items-center space-x-3 bg-white p-4 rounded border border-gray-200 shadow-sm hover:border-orange-500 transition-all duration-200"
                style={{borderRadius: "12px"}}
              >
                <RadioGroupItem
                  value={option.value}
                  id={`${field.id}-${option.id}`}
                  className="h-5 w-5"
                  style={{
                    borderColor: "var(--color-secondary)",
                    "&:checked": {backgroundColor: "var(--color-secondary)"}
                  }}
                />
                <Label
                  htmlFor={`${field.id}-${option.id}`}
                  className="flex-grow cursor-pointer text-gray-700"
                >
                  {option.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        );

      case "checkbox":
        return (
          <div
            className="flex items-center space-x-3 bg-white p-4 rounded border border-gray-200 shadow-sm hover:border-orange-500 transition-all duration-200"
            style={{borderRadius: "12px"}}
          >
            <Checkbox
              id={field.id}
              checked={formValues[field.id] || false}
              onCheckedChange={(checked) =>
                handleInputChange(field.id, checked)
              }
              required={field.required}
              disabled={formSubmitted}
              className="h-5 w-5"
              style={{
                borderColor: "var(--color-secondary)",
                "&:checked": {backgroundColor: "var(--color-secondary)"}
              }}
            />
            <Label htmlFor={field.id} className="cursor-pointer text-gray-700">
              {field.placeholder || "Acepto"}
            </Label>
          </div>
        );

      // Añadir el caso para "file"
      case "file":
        return (
          <FileUploadField
            id={field.id}
            label={field.label}
            required={field.required}
            multiple={field.multiple}
            accept={field.accept || ".pdf,.doc,.docx,.jpg,.jpeg,.png"}
            value={formValues[field.id] || []}
            onChange={(files) => {
              handleInputChange(field.id, files);
            }}
            disabled={formSubmitted}
            style={{borderRadius: "8px"}}
          />
        );

      case "date":
        return (
          <Input
            id={field.id}
            type="date"
            placeholder={field.placeholder}
            value={formValues[field.id] || ""}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            required={field.required}
            min={field.minDate}
            max={field.maxDate}
            className="bg-white"
            style={{borderRadius: "8px"}}
            disabled={formSubmitted}
          />
        );

      case "time":
        return (
          <Input
            id={field.id}
            type="time"
            placeholder={field.placeholder}
            value={formValues[field.id] || ""}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            required={field.required}
            min={field.minTime}
            max={field.maxTime}
            className="bg-white"
            style={{borderRadius: "8px"}}
            disabled={formSubmitted}
          />
        );

      default:
        return <p>Tipo de campo no soportado: {field.type}</p>;
    }
  };

  if (formSubmitted && !isPreview) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] animate-fadeIn">
        <Card
          className="shadow-2xl transform transition-all duration-500 animate-scaleIn relative z-[10000]"
          style={{
            borderTop: "4px solid var(--color-secondary)",
            borderRadius: "24px",
            backgroundColor: "white",
            maxWidth: "600px",
            width: "90%"
          }}
        >
          <CardHeader
            className="bg-gradient-to-r from-gray-50 to-white pb-6"
            style={{borderRadius: "24px 24px 0 0"}}
          >
            <div className="flex flex-col items-center text-center space-y-4">
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center mb-4 animate-bounce"
                style={{
                  backgroundColor: "rgba(var(--color-secondary-rgb), 0.1)"
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-10 w-10"
                  style={{color: "var(--color-secondary)"}}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <CardTitle
                className="text-3xl font-bold"
                style={{color: "var(--color-secondary)"}}
              >
                ¡Formulario enviado!
              </CardTitle>
              <CardDescription className="text-lg text-gray-600">
                Gracias por completar el formulario.
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="pt-6 text-center space-y-4">
            <p className="text-gray-600 text-lg">
              Tu información ha sido recibida correctamente.
            </p>
            <div className="flex justify-center space-x-4 mt-6">
              <Button
                onClick={() => {
                  setFormValues({});
                  setFormSubmitted(false);
                }}
                className="px-6 py-3 text-base font-semibold transition-all duration-200 hover:scale-105 bg-orange-500 hover:bg-orange-600"
                style={{
                  color: "white",
                  borderRadius: "12px",
                  border: "none",
                  boxShadow: "0 4px 6px rgba(0,0,0,0.1)"
                }}
              >
                Enviar otro formulario
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <Card
      className="shadow-lg hover:shadow-xl transition-all duration-300 mx-auto"
      style={{
        borderTop: "4px solid var(--color-secondary)",
        borderRadius: "16px",
        width: "100%",
        minWidth: "1500px",
        maxWidth: "1800px",
        backgroundColor: "white"
      }}
    >
      <CardHeader
        className="bg-gradient-to-r from-gray-50 to-white pb-6"
        style={{borderRadius: "16px 16px 0 0"}}
      >
        <CardTitle
          className="text-2xl font-bold mb-2"
          style={{color: "var(--color-secondary)"}}
        >
          {form.title}
          {submissionId && (
            <span className="text-sm font-normal text-gray-500 ml-2">
              (Editando presentación anterior)
            </span>
          )}
        </CardTitle>
        {form.description && (
          <CardDescription className="text-gray-600 text-base">
            {form.description}
          </CardDescription>
        )}
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <div className="min-h-[600px] relative">
          <CardContent className="p-8">
            <div className="space-y-8">
              {form.fields.map((field, index) => {
                const fieldContent = renderField(field);
                if (!fieldContent) return null;

                const isDependedOn = form.fields.some(
                  (f) =>
                    f.conditionalLogic?.enabled &&
                    f.conditionalLogic.dependsOn === field.id
                );

                return (
                  <div
                    key={field.id}
                    className="space-y-4 group animate-fadeIn"
                    style={{
                      animationDelay: `${index * 100}ms`,
                      animationFillMode: "both"
                    }}
                  >
                    <div className="flex flex-col space-y-2">
                      <Label
                        htmlFor={field.id}
                        className="text-sm font-semibold text-gray-700 flex items-center group-hover:text-gray-900 transition-colors duration-200"
                      >
                        {field.label}
                        {field.required && (
                          <span className="text-red-500 ml-1">*</span>
                        )}
                      </Label>
                      {field.helpText && (
                        <p className="text-sm text-gray-500 italic animate-fadeIn">
                          {field.helpText}
                        </p>
                      )}
                    </div>
                    <div className="transform transition-all duration-200 group-hover:translate-x-1">
                      {fieldContent}
                    </div>
                    {index < form.fields.length - 1 && (
                      <Separator className="mt-8 bg-gray-100" />
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </div>
        <CardFooter
          className="bg-gradient-to-r from-gray-50 to-white p-8 flex justify-end sticky bottom-0"
          style={{borderRadius: "0 0 16px 16px"}}
        >
          <Button
            className="w-full md:w-auto px-8 py-6 text-base font-semibold transform transition-all duration-200 hover:scale-[1.02] hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed bg-orange-500 hover:bg-orange-600"
            disabled={isPreview ? false : isSubmitting || formSubmitted}
            style={{
              color: "white",
              borderRadius: "12px",
              border: "none",
              boxShadow: "0 4px 6px rgba(0,0,0,0.1)"
            }}
          >
            {isSubmitting ? (
              <div className="flex items-center space-x-2">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>{submissionId ? "Actualizando..." : "Enviando..."}</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <span>{submissionId ? "Actualizar" : "Enviar"}</span>
              </div>
            )}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}

// Update the styles

